import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, Search as SearchIcon, MapPin, Building2, Eye, UserCircle2, Heart, Bell, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Operator analytics dashboard. Lives behind /admin → "Analytics" tab.
// Calls the admin-gated /api/admin/analytics/overview endpoint and renders
// the four headline KPIs, a daily-trend strip, and four "top N" lists.
// All numbers are pulled live on mount + when the window changes — no
// in-memory caching since the volume is intentionally small.
//
// This component is intentionally separate from /components/AnalyticsTab.jsx,
// which is the per-shop dashboard a DISPENSARY OWNER sees on their own
// dashboard. Operator-wide aggregation (top searches, top ZIPs, total
// visits across the whole site) is a different surface with a different
// endpoint, so we don't share code.

const StatCard = ({ label, value, accent }) => (
  <div
    className="border border-border rounded-2xl p-5 bg-card"
    data-testid={`operator-analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="eyebrow mb-2">{label}</div>
    <div className={`font-serif text-3xl ${accent || ""}`}>
      {value?.toLocaleString?.() ?? value}
    </div>
  </div>
);

// Tiny inline bar chart for the daily-trend strip. Sparkline-style, no
// external chart lib — keeps the bundle small and matches the rest of the
// admin console's plain-card aesthetic. We expose start/end date labels as
// always-visible captions under the chart so the operator can read the
// window at a glance — iOS Safari doesn't fire `title=` tooltips on touch,
// so we can't rely on hover-only affordances.
const _fmtDay = (iso) => {
  // ISO yyyy-mm-dd → "Jun 28"
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mi = parseInt(m, 10) - 1;
  return `${months[mi] ?? m} ${parseInt(d, 10)}`;
};

const TrendStrip = ({ trend }) => {
  const max = useMemo(
    () => Math.max(1, ...trend.map((d) => d.visits)),
    [trend]
  );
  if (!trend.length) {
    return (
      <div className="text-sm text-foreground/50 py-10 text-center">
        No visit data in this window yet.
      </div>
    );
  }
  const startLabel = _fmtDay(trend[0]?.day);
  const endLabel = _fmtDay(trend[trend.length - 1]?.day);
  const totalVisits = trend.reduce((s, d) => s + d.visits, 0);
  return (
    <div data-testid="operator-analytics-trend-strip">
      <div className="flex items-end gap-1 h-32 px-1">
        {trend.map((d) => {
          const pct = Math.max(2, Math.round((d.visits / max) * 100));
          return (
            <div
              key={d.day}
              className="flex-1 bg-emerald-500/70 hover:bg-emerald-400 rounded-t transition-colors"
              style={{ height: `${pct}%`, minWidth: 4 }}
              title={`${d.day} · ${d.visits} visits`}
              data-testid={`operator-analytics-trend-bar-${d.day}`}
            />
          );
        })}
      </div>
      {/* Always-visible caption: start date — end date · total. Replaces the
          iOS-hostile hover tooltips so the operator can read the window at
          a glance on any device. */}
      <div
        className="mt-3 flex items-center justify-between text-xs text-foreground/60"
        data-testid="operator-analytics-trend-caption"
      >
        <span className="font-mono">{startLabel}</span>
        <span className="text-foreground/80">
          {totalVisits.toLocaleString()} visits in this window
        </span>
        <span className="font-mono">{endLabel}</span>
      </div>
    </div>
  );
};

const TopList = ({ title, icon: Icon, rows, renderRow, emptyHint, testid }) => (
  <div className="border border-border rounded-2xl p-5 bg-card" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-emerald-300" />
      <h3 className="font-serif text-lg">{title}</h3>
    </div>
    {rows.length === 0 ? (
      <div className="text-xs text-foreground/50 py-6 text-center">{emptyHint}</div>
    ) : (
      <ol className="space-y-2">
        {rows.map((r, i) => (
          <li
            key={i}
            className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-b-0"
          >
            {renderRow(r, i)}
          </li>
        ))}
      </ol>
    )}
  </div>
);

export const OperatorAnalyticsTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = () => {
    setLoading(true);
    return api
      .get("/admin/analytics/overview", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get("/admin/analytics/overview", { params: { days } })
      .then((r) => live && setData(r.data))
      .catch(() => live && setData(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [days]);

  // Wipe every row from analytics_events + analytics_visits. Confirms
  // twice because it's destructive — but since admin activity is now
  // filtered out at write time, users tend to want to zero the historical
  // pollution once and start fresh.
  const resetAll = async () => {
    const ok = window.confirm(
      "Wipe ALL analytics history?\n\n" +
        "This deletes every impression, click, page view, and search event " +
        "in the database. Future counts start from zero.\n\n" +
        "Admin activity is already excluded going forward — this only clears " +
        "the polluted history from before that fix landed.\n\n" +
        "Continue?"
    );
    if (!ok) return;
    setResetting(true);
    try {
      const r = await api.post("/admin/analytics/reset", null, {
        params: { scope: "all" },
      });
      const d = r.data?.deleted || {};
      toast.success(
        `Analytics reset — ${d.analytics_events || 0} events + ${
          d.analytics_visits || 0
        } visits removed`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="py-20 text-center text-foreground/50">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="py-12 text-center text-sm text-foreground/60">
        Couldn&apos;t load analytics. Try refreshing.
      </div>
    );
  }

  const v = data.visits || {};
  const a = data.accounts || {};

  return (
    <div className="space-y-6" data-testid="operator-analytics-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-accent" />
            Operator analytics
          </div>
          <p className="text-sm text-foreground/60">
            Live patient-side funnel. Updates in real time as visitors land on
            Bud Link.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(days)}
            onValueChange={(v) => setDays(parseInt(v, 10))}
          >
            <SelectTrigger
              data-testid="operator-analytics-window-select"
              className="w-44 bg-card border-border"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
            </SelectContent>
          </Select>
          <button
            type="button"
            data-testid="operator-analytics-reset"
            onClick={resetAll}
            disabled={resetting}
            title="Wipe every analytics row so counts start from zero"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-destructive/40 text-destructive text-xs hover:bg-destructive/10 transition-colors disabled:opacity-50"
          >
            {resetting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
            Reset analytics
          </button>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total visits" value={v.total ?? 0} accent="text-emerald-300" />
        <StatCard label="Last 24h" value={v.today ?? 0} />
        <StatCard label="Last 7 days" value={v.last_7d ?? 0} />
        <StatCard label="Last 30 days" value={v.last_30d ?? 0} />
      </div>

      {/* Accounts row — anonymous-vs-signed-in split. Built so the
          operator can see the opt-in account funnel without forcing
          accounts on anyone (browsing + favorites + stock alerts all
          work without one). */}
      <div className="border border-border rounded-2xl p-5 bg-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <UserCircle2 className="w-4 h-4 text-emerald-300" />
            <h3 className="font-serif text-lg">Accounts</h3>
          </div>
          <span className="text-xs text-foreground/50">
            Accounts are optional · favorites + alerts work anonymously
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Total accounts" value={a.total ?? 0} accent="text-emerald-300" />
          <StatCard label="New 24h" value={a.today ?? 0} />
          <StatCard label="New 7d" value={a.last_7d ?? 0} />
          <StatCard label="New 30d" value={a.last_30d ?? 0} />
          <div className="border border-border rounded-2xl p-5 bg-background" data-testid="operator-analytics-stat-favorites">
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              <Heart className="w-3 h-3" /> Favorites saved
            </div>
            <div className="font-serif text-3xl">{(a.favorites ?? 0).toLocaleString()}</div>
          </div>
          <div className="border border-border rounded-2xl p-5 bg-background" data-testid="operator-analytics-stat-stock-alerts">
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              <Bell className="w-3 h-3" /> Stock alerts
            </div>
            <div className="font-serif text-3xl">{(a.stock_alerts ?? 0).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Daily trend */}
      <div className="border border-border rounded-2xl p-5 bg-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-300" />
            <h3 className="font-serif text-lg">Daily visits — last {data.days} days</h3>
          </div>
        </div>
        <TrendStrip trend={data.trend || []} />
      </div>

      {/* Four-up top lists */}
      <div className="grid md:grid-cols-2 gap-4">
        <TopList
          title="Top searched strains"
          icon={SearchIcon}
          rows={data.top_searches || []}
          emptyHint="No searches logged yet in this window."
          testid="operator-analytics-top-searches"
          renderRow={(r) => (
            <>
              <span className="font-medium truncate pr-3">&ldquo;{r.q}&rdquo;</span>
              <span className="text-emerald-300 font-mono">{r.count.toLocaleString()}</span>
            </>
          )}
        />
        <TopList
          title="Top clicked dispensaries"
          icon={Building2}
          rows={data.top_dispensaries || []}
          emptyHint="No dispensary clicks logged yet."
          testid="operator-analytics-top-dispensaries"
          renderRow={(r) => (
            <>
              <span className="truncate pr-3">
                <span className="font-medium">{r.name}</span>
                {r.city ? <span className="text-foreground/40"> · {r.city}</span> : null}
              </span>
              <span className="text-emerald-300 font-mono">{r.views.toLocaleString()}</span>
            </>
          )}
        />
        <TopList
          title="Top areas"
          icon={MapPin}
          rows={data.top_cities || []}
          emptyHint="No locations set yet. Visitors haven't picked a ZIP."
          testid="operator-analytics-top-cities"
          renderRow={(r) => (
            <>
              <span className="font-medium truncate pr-3">{r.city}</span>
              <span className="text-emerald-300 font-mono">{r.count.toLocaleString()}</span>
            </>
          )}
        />
        <TopList
          title="Most-visited pages"
          icon={Eye}
          rows={data.top_paths || []}
          emptyHint="No page views logged yet."
          testid="operator-analytics-top-paths"
          renderRow={(r) => (
            <>
              <span className="font-mono truncate pr-3 text-foreground/80">{r.path}</span>
              <span className="text-emerald-300 font-mono">{r.count.toLocaleString()}</span>
            </>
          )}
        />
      </div>

      <p className="text-xs text-foreground/40 pt-2">
        Analytics are stored in <code className="text-foreground/60">analytics_visits</code>.
        Nothing is shared with third parties. No IPs or user agents are persisted.
      </p>
    </div>
  );
};

export default OperatorAnalyticsTab;
